import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { selectOrg } from '../../selectors/selectors';
import { OrgComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { globalMessage } from '../../utils/globalMessage';
import { SettingsSwitchRow } from '../SettingsSwitchRow';
import { useError, useIsLoading } from '@/utils/hooks/helpers';

interface MergeUsersByDomainSettingProps extends OrgComponentProps {
    onUpdateMergeAccountsSetting: (enabled: boolean) => void;
}

const MergeUsersByDomainSetting: React.FC<MergeUsersByDomainSettingProps> = (props: MergeUsersByDomainSettingProps) => {
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));

    const allowedDomains = org.allowedDomains;
    const settingEnabled = org.mergeAccountsWithAllowedDomainEnabled;

    const isLoading = useIsLoading(OrgSettingsUpdatable.MergeAccountsSetting);
    const error = useError(OrgSettingsUpdatable.MergeAccountsSetting);

    const onSwitchStateChanged = (checked: boolean) => {
        setIsFieldChecked(checked);
        props.onUpdateMergeAccountsSetting(checked);
    };

    const [isFieldChecked, setIsFieldChecked] = React.useState(settingEnabled);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
            setIsFieldChecked(settingEnabled);
        }
    }, [error]);

    React.useEffect(() => {
        setIsFieldChecked(settingEnabled);
    }, [settingEnabled]);

    return (
        <SettingsSwitchRow
            title="New users with matching email domains join this organization"
            description={
                <>
                    Your teammates will be automatically added to this organization if they sign up with an email from
                    the domains:{' '}
                    <Text type="secondary" strong>
                        {allowedDomains.join(', ')}
                    </Text>
                    .
                </>
            }
            isLoading={isLoading}
            checked={isFieldChecked}
            tooltipText="When this setting is enabled, teammates can join you in Runway by visiting app.runway.team and signing up with one of the listed email domains."
            onChange={onSwitchStateChanged}
            switchTooltipText={
                org.ssoSamlConfig.ssoDirectory?.isActive ? 'User provisioning is being managed by Directory Sync' : null
            }
        />
    );
};

export default MergeUsersByDomainSetting;
